import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getMyPlans } from '../services/planService';

const SavePlanContext = createContext();

export const SavePlanProvider = ({ children }) => {
  const { user } = useAuth();

  // Core state
  const [hasSkippedSave, setHasSkippedSave] = useState(false);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const [hasExistingPlan, setHasExistingPlan] = useState(false);
  const [existingPlan, setExistingPlan] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Deduplication: track last load time to prevent multiple rapid loads
  const lastLoadTime = useRef(0);
  const isLoadingRef = useRef(false);
  const LOAD_DEBOUNCE_MS = 1000; // Minimum 1 second between loads

  // Check for existing plan when user logs in
  useEffect(() => {
    const checkExistingPlan = async () => {
      if (user) {
        // Debounce initial load too
        const now = Date.now();
        if (now - lastLoadTime.current < LOAD_DEBOUNCE_MS) {
          return;
        }
        lastLoadTime.current = now;

        setIsLoadingPlan(true);
        const result = await getMyPlans();
        if (result.success && result.data && result.data.length > 0) {
          setHasExistingPlan(true);
          setExistingPlan(result.data[0]);
          setHasSavedOnce(true);
        } else {
          setHasExistingPlan(false);
          setExistingPlan(null);
        }
        setIsLoadingPlan(false);
      } else {
        setHasExistingPlan(false);
        setExistingPlan(null);
        setHasSavedOnce(false);
      }
    };
    checkExistingPlan();
  }, [user]);

  // Show toast notification
  const triggerToast = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  // Mark as skipped (user clicked "Continue without saving")
  const markAsSkipped = useCallback(() => {
    setHasSkippedSave(true);
  }, []);

  // Mark as saved (after successful save)
  const markAsSaved = useCallback(
    (plan = null) => {
      setHasSavedOnce(true);
      setHasSkippedSave(false);
      if (plan) {
        setExistingPlan(plan);
        setHasExistingPlan(true);
      }
      triggerToast('✓ Plan saved successfully');
    },
    [triggerToast]
  );

  // Mark as updated (for returning users)
  const markAsUpdated = useCallback(() => {
    triggerToast('✓ Plan updated');
  }, [triggerToast]);

  // Sidebar controls
  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  // Reset state (on logout)
  const reset = useCallback(() => {
    setHasSkippedSave(false);
    setHasSavedOnce(false);
    setHasExistingPlan(false);
    setExistingPlan(null);
    setIsSidebarOpen(false);
    lastLoadTime.current = 0;
  }, []);

  // Refresh plan data - WITH DEDUPLICATION
  const refreshPlan = useCallback(async () => {
    if (!user) return;

    // Debounce: skip if we loaded recently
    const now = Date.now();
    if (now - lastLoadTime.current < LOAD_DEBOUNCE_MS) {
      console.log('SavePlanContext: Skipping refresh, too soon since last load');
      return;
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      console.log('SavePlanContext: Skipping refresh, already loading');
      return;
    }

    isLoadingRef.current = true;
    lastLoadTime.current = now;
    setIsLoadingPlan(true);

    try {
      const result = await getMyPlans();
      if (result.success && result.data && result.data.length > 0) {
        setExistingPlan(result.data[0]);
        setHasExistingPlan(true);
      }
    } catch (error) {
      console.error('SavePlanContext: Error refreshing plan:', error);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingPlan(false);
    }
  }, [user]);

  // Should show save prompt (after Freedom Number, for users who haven't saved)
  const shouldShowSavePrompt = !user || (!hasExistingPlan && !hasSavedOnce);

  // Should show persistent banner (user skipped save and hasn't saved yet)
  const shouldShowBanner = user ? false : hasSkippedSave && !hasSavedOnce;

  return (
    <SavePlanContext.Provider
      value={{
        // State
        hasSkippedSave,
        hasSavedOnce,
        hasExistingPlan,
        existingPlan,
        isSidebarOpen,
        shouldShowSavePrompt,
        shouldShowBanner,
        isLoadingPlan,

        // Toast
        showToast,
        toastMessage,
        isSaving,
        setIsSaving,
        triggerToast,

        // Actions
        markAsSkipped,
        markAsSaved,
        markAsUpdated,
        openSidebar,
        closeSidebar,
        reset,
        refreshPlan,
      }}
    >
      {children}
    </SavePlanContext.Provider>
  );
};

export const useSavePlan = () => {
  const context = useContext(SavePlanContext);
  if (!context) {
    throw new Error('useSavePlan must be used within SavePlanProvider');
  }
  return context;
};

export default SavePlanContext;
